import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { AiOutlinePlus } from "react-icons/ai";
import { BsCheckCircleFill, BsTrash } from "react-icons/bs";
import Label from "../../../../../../components/form_fields/Label";
import { IdentityCode } from "../../../../../../models/payment-setup.dto";
import { FieldError, FileUploadField, PhoneField, SectionHeading } from "../components";
import GhanaCardVerificationModal from "../GhanaCardVerificationModal";
import {
	EMAIL_PATTERN,
	GHANA_CARD_PATTERN,
	PaymentSetupFormValues,
	emptyDirector,
	inputClassName,
} from "../helpers";

const DirectorsStep = () => {
	const {
		register,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<PaymentSetupFormValues>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "companyDirectors",
		rules: {
			validate: (directors) => {
				const ghanaCardDirectors = directors.filter(
					(director) => director.identityCode === IdentityCode.ghana_card,
				);
				if (
					ghanaCardDirectors.length &&
					!ghanaCardDirectors.some((director) => director.verificationId)
				) {
					return "Verify at least one director's Ghana Card before submitting";
				}
				return true;
			},
		},
	});

	const directors = watch("companyDirectors");
	const [verifyingIndex, setVerifyingIndex] = useState<number | null>(null);
	const verifyingDirector = verifyingIndex !== null ? directors[verifyingIndex] : undefined;

	return (
		<div>
			<SectionHeading
				title="Company directors"
				description="Add at least one director. Enter names exactly as they appear on their ID."
			/>
			<div className="space-y-5">
				{fields.map((field, index) => {
					const director = directors[index] || emptyDirector;
					const directorErrors = errors.companyDirectors?.[index];
					const isGhanaCard = director.identityCode === IdentityCode.ghana_card;
					const isPassport = director.identityCode === IdentityCode.passport;

					return (
						<div key={field.id} className="rounded-lg border border-gray-200 p-5">
							<div className="flex justify-between items-center mb-4">
								<h4 className="text-sm font-semibold text-gray-700">
									Director {index + 1}
								</h4>
								{fields.length > 1 && (
									<button
										type="button"
										onClick={() => remove(index)}
										className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500"
									>
										<BsTrash /> Remove
									</button>
								)}
							</div>
							<div className="grid grid-cols-2 gap-x-6 gap-y-5">
								<div>
									<Label for={`companyDirectors.${index}.lastName`} title="Surname" />
									<input
										id={`companyDirectors.${index}.lastName`}
										placeholder="Enter surname"
										{...register(`companyDirectors.${index}.lastName`, {
											required: "Enter the director's surname",
											// A name change invalidates the card comparison
											onChange: () =>
												isGhanaCard &&
												setValue(`companyDirectors.${index}.verificationId`, ""),
										})}
										className={inputClassName(!!directorErrors?.lastName)}
									/>
									<FieldError error={directorErrors?.lastName} />
								</div>
								<div>
									<Label for={`companyDirectors.${index}.otherNames`} title="Other names" />
									<input
										id={`companyDirectors.${index}.otherNames`}
										placeholder="Enter other names"
										{...register(`companyDirectors.${index}.otherNames`, {
											required: "Enter the director's other names",
											onChange: () =>
												isGhanaCard &&
												setValue(`companyDirectors.${index}.verificationId`, ""),
										})}
										className={inputClassName(!!directorErrors?.otherNames)}
									/>
									<FieldError error={directorErrors?.otherNames} />
								</div>
								<div>
									<Label for={`companyDirectors.${index}.email`} title="Email" />
									<input
										id={`companyDirectors.${index}.email`}
										type="email"
										placeholder="name@company.com"
										{...register(`companyDirectors.${index}.email`, {
											required: "Enter the director's email",
											pattern: {
												value: EMAIL_PATTERN,
												message: "Enter a valid email address",
											},
										})}
										className={inputClassName(!!directorErrors?.email)}
									/>
									<FieldError error={directorErrors?.email} />
								</div>
								<div>
									<Label for={`companyDirectors.${index}.phoneNumber`} title="Phone number" />
									<PhoneField
										name={`companyDirectors.${index}.phoneNumber`}
										control={control}
										required="Enter the director's phone number"
										error={directorErrors?.phoneNumber}
									/>
								</div>
								<div>
									<Label for={`companyDirectors.${index}.identityCode`} title="ID type" />
									<select
										id={`companyDirectors.${index}.identityCode`}
										{...register(`companyDirectors.${index}.identityCode`, {
											required: "Select an ID type",
											onChange: () => {
												setValue(`companyDirectors.${index}.identity`, "");
												setValue(`companyDirectors.${index}.verificationId`, "");
											},
										})}
										className={inputClassName(!!directorErrors?.identityCode)}
									>
										<option value="">Select ID type</option>
										<option value={IdentityCode.ghana_card}>Ghana Card</option>
										<option value={IdentityCode.passport}>Passport</option>
									</select>
									<FieldError error={directorErrors?.identityCode} />
								</div>
								<div>
									{isGhanaCard && (
										<>
											<Label for={`companyDirectors.${index}.identity`} title="Ghana Card number" />
											<input
												id={`companyDirectors.${index}.identity`}
												placeholder="GHA-123456789-0"
												{...register(`companyDirectors.${index}.identity`, {
													required: "Enter the Ghana Card number",
													pattern: {
														value: GHANA_CARD_PATTERN,
														message: "Use the format GHA-123456789-0",
													},
													setValueAs: (value) =>
														typeof value === "string" ? value.toUpperCase().trim() : value,
													onChange: () =>
														setValue(`companyDirectors.${index}.verificationId`, ""),
												})}
												className={inputClassName(!!directorErrors?.identity)}
											/>
											<FieldError error={directorErrors?.identity} />
										</>
									)}
									{isPassport && (
										<>
											<Label for={`companyDirectors.${index}.identity`} title="Passport bio-data page" />
											<Controller
												name={`companyDirectors.${index}.identity`}
												control={control}
												rules={{ required: "Upload the passport bio-data page" }}
												render={({ field }) => (
													<FileUploadField
														value={field.value}
														onChange={field.onChange}
														accept=".png,.jpg,.jpeg,.pdf"
														buttonLabel="Upload passport"
														hasError={!!directorErrors?.identity}
													/>
												)}
											/>
											<FieldError error={directorErrors?.identity} />
										</>
									)}
								</div>
							</div>

							{isGhanaCard && (
								<div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
									{director.verificationId ? (
										<span className="flex items-center gap-2 text-sm text-green-700">
											<BsCheckCircleFill /> Ghana Card verified
										</span>
									) : (
										<span className="text-xs text-gray-600">
											At least one Ghana Card director must be verified to submit, and all
											of them before the merchant can go live.
										</span>
									)}
									<button
										type="button"
										disabled={!GHANA_CARD_PATTERN.test(director.identity || "")}
										onClick={() => setVerifyingIndex(index)}
										className="shrink-0 ml-4 h-10 px-4 border border-gray-300 rounded-lg text-xs text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50"
									>
										{director.verificationId ? "Verify again" : "Verify Ghana Card"}
									</button>
								</div>
							)}
						</div>
					);
				})}

				<FieldError error={errors.companyDirectors?.root} />

				<button
					type="button"
					onClick={() => append({ ...emptyDirector })}
					className="flex items-center gap-1 text-sm text-primary-blue"
				>
					<AiOutlinePlus /> Add director
				</button>
			</div>

			{verifyingIndex !== null && verifyingDirector && (
				<GhanaCardVerificationModal
					open
					onClose={() => setVerifyingIndex(null)}
					ghanaCardNumber={verifyingDirector.identity}
					directorName={verifyingDirector}
					onVerified={(verificationId) =>
						setValue(`companyDirectors.${verifyingIndex}.verificationId`, verificationId, {
							shouldValidate: true,
						})
					}
				/>
			)}
		</div>
	);
};

export default DirectorsStep;
